import React, { useState, useEffect } from 'react';
import { Form, Select, Button, message, Flex, Breadcrumb, Card, Descriptions, Tag, Table, AutoComplete } from 'antd';
import { getApi, postApi } from '../services/api.jsx';

const userGroups = JSON.parse(localStorage.getItem("userGroups") || "[]");
const isAdmin = userGroups.includes("admin");

const KeyManagement = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [keys, setKeys] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);

  const columns = [
    {
      title: 'Type',
      dataIndex: 'keyType',
      key: 'keyType',
    },
    {
      title: 'Value',
      dataIndex: 'keyValue',
      key: 'keyValue',
      render: (text) => <span>{text}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      render: (active) => (
        <Tag color={active ? 'success' : 'error'}>
          {active ? 'Active' : ''}
        </Tag>
      ),
    },
  ];

  useEffect(() => {
    fetchKeys();
  }, []);

  const fetchKeys = () => {
    setLoading(true);
    getApi('/key/list')
      .then((response) => {
        setKeys(response);
      })
      .catch((error) => {
        console.error('Error fetching keys:', error);
        message.error('Failed to fetch keys');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchKeyDetailsForType = (keyType) => {
    setLoading(true);
    const keyForType = keys.find(key => key.keyType === keyType && key.active);

    if (keyForType) {
      getApi(`/key/${keyForType.id}`)
        .then((response) => {
          setSelectedKey(response);
          form.setFieldsValue({
            keyType: response.keyType,
            keyValue: response.keyValue
          });
        })
        .catch((error) => {
          form.resetFields();
          setSelectedKey(null);
          message.error(`Failed to load key details for ${keyType}`);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      form.resetFields();
      setSelectedKey(null);
      setLoading(false);
    }
  };

  const handleSubmit = (values) => {
    setLoading(true);
    const requestBody = {
      ...selectedKey,
      keyValue: values.keyValue,
      keyType: values.keyType,
      active: true
    };
    
    if (!selectedKey || !selectedKey.id) {
      message.error("Cannot update: No key selected or loaded.");
      setLoading(false);
      return;
    }

    postApi('/key', requestBody)
      .then((response) => {
        message.success('Secret key updated successfully');
        setSelectedKey(response);
        fetchKeys();
      })
      .catch((error) => {
        console.error('Error setting key:', error);
        message.error('Failed to update secret key');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleKeyTypeChange = (keyType) => {
    form.setFieldsValue({ keyValue: undefined });
    setSelectedKey(null);
    if (keyType) {
      fetchKeyDetailsForType(keyType);
    } else {
      form.resetFields();
      setSelectedKey(null);
    }
  };

  const handleKeyValueChange = (keyValue) => {
    const selectedType = form.getFieldValue('keyType');
    const selected = keys.find(
      key => key.keyValue === keyValue && key.keyType === selectedType
    );
    if (selected) {
      setSelectedKey(selected);
      form.setFieldsValue({ keyType: selected.keyType });
    } else {
      setSelectedKey(prev => {
        if (!prev) {
          return { keyValue: keyValue, keyType: selectedType };
        }
        return { ...prev, keyValue: keyValue };
      });
    }
  };

  const getKeyTypeOptions = () => {
    const uniqueKeyTypes = [...new Set(keys.map(key => key.keyType))];
    return uniqueKeyTypes
      .filter(type => type !== "DESKTOP_VIEW")
      .map(type => ({
        label: type,
        value: type
      }));
  };

  const getKeyValueOptions = () => {
    const selectedType = form.getFieldValue('keyType');
    if (!selectedType) return [];

    const keysForSelectedType = keys.filter(key => key.keyType === selectedType);

    const uniqueKeyValues = [...new Set(keysForSelectedType.map(key => key.keyValue))];

    return uniqueKeyValues.map(value => ({
      value: value,
      label: value
    }));
  };

  return (
    <div style={{ padding: '24px' }}>
    <Flex
        gap="middle"
        align="center"
        justify={"space-between"}
        className="page-header"
      >
        <h2>Key Management</h2>
        <Breadcrumb
          items={[
            {
              title: "Home",
            },
            {
              title: "Management",
            },
            {
              title: <a href="">Secret Key</a>,
            },
          ]}
        />
      </Flex>

      <Flex gap="large" style={{ marginTop: '24px' }}>
        <Card style={{ width: '45%' }}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Form.Item
              name="keyType"
              label="Key Type"
              rules={[{ required: true, message: 'Please select a key type' }]}
            >
              <Select
                placeholder="Select a key type"
                loading={loading}
                options={getKeyTypeOptions()}
                onChange={handleKeyTypeChange}
                allowClear
              />
            </Form.Item>

            <Form.Item
              name="keyValue"
              label="Key Value"
              rules={[{ required: true, message: 'Please select or enter a key value' }]}
            >
              <AutoComplete
                placeholder="Select or enter a key value"
                options={getKeyValueOptions()}
                onChange={handleKeyValueChange}
                allowClear
                filterOption={(inputValue, option) =>
                  option.value.toLowerCase().includes(inputValue.toLowerCase())
                }
              />
            </Form.Item>

            
            {selectedKey && !selectedKey.id && (
              <Descriptions bordered column={1}>
                <Descriptions.Item label="Key Value">{selectedKey.keyValue}</Descriptions.Item>
                <Descriptions.Item label="Key Type">{selectedKey.keyType}</Descriptions.Item>
              </Descriptions>
            )}

            <Form.Item style={{ marginTop: '24px' }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                disabled={!form.getFieldValue('keyType') || !form.getFieldValue('keyValue')}
              >
                Update Secret Key
              </Button>
            </Form.Item>
          </Form>
        </Card>

        {isAdmin && (
          <Card style={{ width: '55%' }}>
            <h3 style={{ marginBottom: '16px' }}>Default Secret Keys</h3>
            <Table
              columns={columns}
              dataSource={keys.filter(key => key.active && key.keyType !== "DESKTOP_VIEW")}
              rowKey="id"
              pagination={false}
              loading={loading}
              scroll={{ x: true }}
            />
          </Card>
        )}
      </Flex>
    </div>
  );
};

export default KeyManagement; 